import React, { useEffect, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { Award, Shield, Truck, DollarSign, Clock, Heart, CheckCircle, Sparkles } from 'lucide-react';

function WhyChooseUs({ isMobile }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const controls = useAnimation();

  const features = [
    {
      id: 1,
      title: "Competitive Prices",
      description: "Best market prices without compromising on premium quality. We beat competitor pricing by 15% guaranteed.",
      icon: DollarSign,
      gradient: "from-emerald-500 to-teal-400",
      bgGradient: "from-emerald-900/20 to-teal-900/10",
      stats: "15% Cheaper",
      delay: 0
    },
    {
      id: 2,
      title: "Fresh & Hygienic",
      description: "Certified HACCP standards with daily sanitation. Our facilities are inspected weekly for optimal hygiene.",
      icon: Shield,
      gradient: "from-blue-500 to-cyan-400",
      bgGradient: "from-blue-900/20 to-cyan-900/10",
      stats: "99.9% Clean",
      delay: 0.1
    },
    {
      id: 3,
      title: "Top Quality",
      description: "Premium grade cuts selected by master butchers. Every product undergoes rigorous quality checks.",
      icon: Award,
      gradient: "from-amber-500 to-orange-400",
      bgGradient: "from-amber-900/20 to-orange-900/10",
      stats: "A+ Grade",
      delay: 0.2
    },
    {
      id: 4,
      title: "Farm to Butchery",
      description: "Direct sourcing from trusted local farms. Complete traceability from pasture to plate within 24 hours.",
      icon: Truck,
      gradient: "from-purple-500 to-pink-400",
      bgGradient: "from-purple-900/20 to-pink-900/10",
      stats: "24h Fresh",
      delay: 0.3
    },
    {
      id: 5,
      title: "Expert Butchers",
      description: "20+ years of combined experience in artisanal butchery. Traditional techniques meet modern standards.",
      icon: Sparkles,
      gradient: "from-red-500 to-rose-400",
      bgGradient: "from-red-900/20 to-rose-900/10",
      stats: "20+ Years",
      delay: 0.4
    },
    {
      id: 6,
      title: "Customer First",
      description: "24/7 customer support and satisfaction guarantee. Custom cutting services available on demand.",
      icon: Heart,
      gradient: "from-violet-500 to-indigo-400",
      bgGradient: "from-violet-900/20 to-indigo-900/10",
      stats: "100% Happy",
      delay: 0.5
    }
  ];

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { y: 60, opacity: 0, rotateX: 15 },
    visible: {
      y: 0,
      opacity: 1,
      rotateX: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 20
      }
    },
    hover: {
      y: -15,
      rotateX: 5,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const floatAnimation = {
    y: [0, -20, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const pulseAnimation = {
    scale: [1, 1.1, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  return (
    <section 
      className="relative py-24 md:py-32 overflow-hidden"
      style={{
        background: 'radial-gradient(circle at 50% 50%, #0a0a0a 0%, #000000 100%)'
      }}
      ref={ref}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Geometric Pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        {/* Gradient Orbs */}
        <motion.div 
          animate={floatAnimation}
          className="absolute top-20 left-10 w-64 h-64 rounded-full bg-gradient-to-r from-emerald-500/10 to-teal-500/10 blur-3xl"
        ></motion.div>
        <motion.div 
          animate={{ ...floatAnimation, transition: { ...floatAnimation.transition, delay: 1 } }}
          className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-gradient-to-r from-amber-500/10 to-orange-500/10 blur-3xl"
        ></motion.div>
        <motion.div 
          animate={{ ...floatAnimation, transition: { ...floatAnimation.transition, delay: 2 } }}
          className="absolute top-1/2 left-1/3 w-96 h-96 rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 blur-3xl"
        ></motion.div>
      </div>

      {/* Floating Icons */}
      <motion.div 
        animate={floatAnimation}
        className="absolute top-32 right-20 text-5xl opacity-10 hidden lg:block"
      >
        🏆
      </motion.div>
      <motion.div 
        animate={{ ...floatAnimation, transition: { ...floatAnimation.transition, delay: 0.5 } }}
        className="absolute bottom-32 left-20 text-5xl opacity-10 hidden lg:block"
      >
        ⭐
      </motion.div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <motion.div
          variants={titleVariants}
          initial="hidden"
          animate={controls}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <motion.div
              animate={pulseAnimation}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-red-500 rounded-full blur-md"></div>
              <div className="relative bg-gradient-to-r from-amber-500 to-red-500 text-white px-6 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                <Award className="w-4 h-4" />
                PREMIUM EXCELLENCE
              </div>
            </motion.div>
          </div>

          <h2 className={`font-bold mb-6 ${isMobile ? 'text-4xl' : 'text-6xl'}`}>
            <span className="bg-gradient-to-r from-amber-200 via-white to-amber-200 bg-clip-text text-transparent">
              Why Choose
            </span>
            <span className="block bg-gradient-to-r from-red-200 via-white to-red-200 bg-clip-text text-transparent">
              Sir Henry Butchery?
            </span>
          </h2>
          
          <motion.p 
            variants={titleVariants}
            className="text-gray-400 text-xl max-w-3xl mx-auto mb-16 leading-relaxed"
          >
            We don't just sell meat - we deliver an experience. Every cut tells a story of quality, 
            passion, and dedication to excellence that sets us apart.
          </motion.p>

          {/* Stats Banner */}
        {/*  <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-20"
          >
            {[
              { value: "5000+", label: "Happy Customers", icon: "😊" },
              { value: "15+", label: "Years Experience", icon: "📅" },
              { value: "99.7%", label: "Satisfaction Rate", icon: "⭐" },
              { value: "24/7", label: "Fresh Supply", icon: "🕒" },
            ].map((stat, index) => (
              <motion.div
                key={index}
                variants={cardVariants}
                className="bg-gradient-to-br from-gray-900 to-black p-6 rounded-2xl border border-gray-800 text-center group hover:border-amber-500/30 transition-all duration-300"
              >
                <div className="text-3xl mb-2 transform group-hover:scale-110 transition-transform duration-300">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold bg-gradient-to-r from-amber-300 to-red-300 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-gray-400 text-sm mt-2">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
          */}
        </motion.div>
        

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-8`}
        >
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.id}
                variants={cardVariants}
                whileHover="hover"
                custom={feature.delay}
                className="relative group"
              >
                {/* Glow Effect */}
                <div className={`absolute -inset-1 bg-gradient-to-r ${feature.gradient} rounded-3xl blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-500`}></div>
                
                {/* Main Card */}
                <div className="relative h-full bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden border border-gray-800 p-8 transition-all duration-300 group-hover:border-gray-700">
                  {/* Animated Background Pattern */}
                  <div className={`absolute inset-0 opacity-5 ${feature.bgGradient}`}></div>
                  
                  {/* Icon Container */}
                  <motion.div
                    animate={floatAnimation}
                    className="relative mb-8"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} rounded-2xl blur-xl opacity-20`}></div>
                    <div className={`relative w-16 h-16 rounded-2xl bg-gradient-to-r ${feature.gradient} flex items-center justify-center shadow-2xl`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                  </motion.div>

                  {/* Stats Badge */}
                  <div className="absolute top-6 right-6">
                    <div className={`px-3 py-1 rounded-full bg-gradient-to-r ${feature.gradient} text-white text-xs font-bold`}>
                      {feature.stats}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-white mb-4 relative">
                    {feature.title}
                    <div className={`absolute -bottom-2 left-0 w-12 h-1 bg-gradient-to-r ${feature.gradient} rounded-full`}></div>
                  </h3>
                  
                  <p className="text-gray-400 mb-8 leading-relaxed">
                    {feature.description}
                  </p>

                  {/* Feature List */}
                  <div className="space-y-3">
                    {[
                      "Premium Quality Guarantee",
                      "Certified & Insured",
                      "24/7 Customer Support",
                      "Money Back Promise"
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <CheckCircle className={`w-5 h-5 bg-gradient-to-r ${feature.gradient} bg-clip-text text-transparent`} />
                        <span className="text-gray-300 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>

                  {/* Hover Effect Line */}
                  <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.gradient} transform origin-left transition-transform duration-300 group-hover:scale-x-100 scale-x-0`}></div>
                </div>

                {/* Floating Number */}
                <div className={`absolute -top-3 -right-3 w-10 h-10 rounded-full bg-gradient-to-r ${feature.gradient} text-white flex items-center justify-center font-bold text-sm shadow-2xl`}>
                  0{feature.id}
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Trust Badges */}
       {/* <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="mt-24 bg-gradient-to-r from-gray-900 to-black rounded-3xl p-8 md:p-12 border border-gray-800 relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 via-amber-500 to-red-500"></div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            <div className="text-center">
              <div className="text-4xl mb-4">🥇</div>
              <div className="text-white font-bold">Certified Quality</div>
              <div className="text-gray-400 text-sm">ISO 9001 Certified</div>
            </div>
            
            <div className="text-center">
              <div className="text-4xl mb-4">🏆</div>
              <div className="text-white font-bold">Award Winning</div>
              <div className="text-gray-400 text-sm">3 Years Running</div>
            </div>
            
            <div className="text-center">
              <div className="text-4xl mb-4">🛡️</div>
              <div className="text-white font-bold">100% Safe</div>
              <div className="text-gray-400 text-sm">HACCP Certified</div>
            </div>
            
            <div className="text-center">
              <div className="text-4xl mb-4">💚</div>
              <div className="text-white font-bold">Eco Friendly</div>
              <div className="text-gray-400 text-sm">Sustainable Practices</div>
            </div>
          </div>
        </motion.div>
        */}

        {/* CTA Section */}
        <motion.div
          variants={titleVariants}
          initial="hidden"
          animate={controls}
          className="mt-24 text-center"
        >
          <div className="inline-flex items-center gap-4 bg-gradient-to-r from-gray-900 to-black px-8 py-4 rounded-full border border-gray-800 mb-8">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-emerald-400 text-sm font-bold">READY TO SERVE</span>
            </div>
            <span className="text-gray-400">|</span>
            <span className="text-gray-300">Join the large community of our satisfied customers</span>
          </div>
          
          <h3 className="text-3xl font-bold text-white mb-6">
            Experience the Sir Henry Difference
          </h3>
          
        {/*  <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-10 py-4 rounded-xl bg-gradient-to-r from-amber-600 to-red-600 text-white font-bold text-lg shadow-2xl shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300 flex items-center gap-3 mx-auto"
          >
            <Sparkles className="w-5 h-5" />
            Order Premium Quality Now
            <Sparkles className="w-5 h-5" />
          </motion.button>
          */}
        </motion.div>
      </div>

      {/* Custom CSS */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        .feature-card-glow {
          box-shadow: 
            0 10px 40px rgba(0, 0, 0, 0.3),
            0 5px 15px rgba(0, 0, 0, 0.2);
        }
        
        .feature-card-glow:hover {
          box-shadow: 
            0 25px 50px rgba(var(--color-primary), 0.15),
            0 15px 30px rgba(var(--color-secondary), 0.1);
        }
        
        .gradient-text-animate {
          background: linear-gradient(
            45deg,
            #fbbf24,
            #dc2626,
            #fbbf24
          );
          background-size: 200% 200%;
          animation: gradientShift 3s ease infinite;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </section>
  );
}

export default WhyChooseUs;